import React, { useContext, useEffect, useState } from 'react';
import {
	Button,
	Checkbox,
	FormControlLabel,
	IconButton,
	MenuItem,
	OutlinedInput,
	Select,
	Typography,
} from '@material-ui/core';
import SubscriptionsIcon from '@material-ui/icons/Subscriptions';
import CloseIcon from '@material-ui/icons/Close';
import VideocamIcon from '@material-ui/icons/Videocam';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { loadPlayerConfiguration } from '../../../../../../actions/playerConfigurationActions';
import { SortableItemContext } from '../ContentItem';

type HeroVideoContentProps = {
	item: any;
};

const HeroVideoContent = ({ item }: HeroVideoContentProps) => {
	const dispatch = useDispatch() as any;
	const { t } = useTranslation();
	const { defaultAccountId } = useSelector((state: any) => state.session);

	const {
		id,
		mediaTitle,
		isShowTextBox,
		contentHeroHeader,
		contentHeroDescription,
		playerConfigId,
		hasAttachedForm,
		useAttachedForm,
		enableSimpleHeroHeading,
	} = item;
	const { attributes, listeners, ref } = useContext(SortableItemContext);
	const {
		miniMediaLibraryRef,
		expandedId,
		handleClearSelectedHeroMedia,
		handleUpdateHeroMedia,
		handleDeleteContentItem,
		setExpandedId,
	} = useContext(SortableItemContext);

	const isExpand = expandedId === id;

	const [playerConfigs, setPlayerConfigs] = useState<any[]>([]);
	const loadPlayerConfig = () => {
		dispatch(loadPlayerConfiguration(defaultAccountId)).then((data: any) => {
			if (!data || typeof data === 'string') {
				setPlayerConfigs([]);
				return;
			}
			setPlayerConfigs(data);
		});
	};

	useEffect(() => {
		loadPlayerConfig();
	}, []);

	return (
		<>
			<div
				{...attributes}
				{...listeners}
				ref={ref}
				style={{
					border: '1px solid #909CA5',
					borderTopLeftRadius: 4,
					borderTopRightRadius: 4,
					borderBottomLeftRadius: isExpand ? 0 : 4,
					borderBottomRightRadius: isExpand ? 0 : 4,
					cursor: 'pointer',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
				onClick={() => {
					setExpandedId(expandedId === id ? '' : id);
				}}
			>
				<div
					style={{
						display: 'flex',
						justifyContent: 'start',
						alignItems: 'center',
						gap: 10,
						padding: '14px 0 14px 10px',
					}}
				>
					<SubscriptionsIcon />
					<Typography variant="body1">{mediaTitle ? mediaTitle : 'Hero video'}</Typography>
				</div>
				<div style={{ display: 'flex' }}>
					<Button
						style={{ minWidth: 0, padding: '14px 10px', borderRadius: 0, borderLeft: '1px solid #909CA5' }}
						onClick={(e) => {
							e.stopPropagation();
							e.preventDefault();
							handleDeleteContentItem(id);
						}}
					>
						<CloseIcon fontSize="small" />
					</Button>
				</div>
			</div>
			{isExpand && (
				<div
					style={{
						borderLeft: '1px solid #909CA5',
						borderRight: '1px solid #909CA5',
						borderBottom: '1px solid #909CA5',
						borderTopLeftRadius: 0,
						borderTopRightRadius: 0,
						borderBottomLeftRadius: 4,
						borderBottomRightRadius: 4,
						padding: '14px 10px',
					}}
				>
					<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
						<div style={{ display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
							<div
								style={{
									width: 33,
									height: 33,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<VideocamIcon fontSize="medium" />
							</div>
							<Typography
								variant="body1"
								style={{
									width: '200px',
									overflow: 'hidden',
									whiteSpace: 'nowrap',
									textOverflow: 'ellipsis',
								}}
							>
								{mediaTitle ? mediaTitle : 'Select a video'}
							</Typography>
						</div>
						<div style={{ display: 'flex', gap: 10, justifyContent: 'end', alignItems: 'center' }}>
							<Button
								variant="contained"
								size="small"
								color="primary"
								style={{ minWidth: 0 }}
								onClick={() => miniMediaLibraryRef?.current?.showMediaLibrary(true)}
							>
								Edit
							</Button>
							<IconButton size="small" onClick={handleClearSelectedHeroMedia}>
								<CloseIcon fontSize="small" />
							</IconButton>
						</div>
					</div>

					<FormControlLabel
						className={'inspect-media__input--full'}
						style={{ display: 'flex', marginLeft: 10 }}
						control={
							<Select
								value={playerConfigId || 'placeholder'}
								variant="outlined"
								fullWidth
								onChange={(event) => {
									handleUpdateHeroMedia('playerConfigId', event.target.value as string);
								}}
							>
								<MenuItem key={0} value={'placeholder'}>
									- Choose a player preset for hero video -
								</MenuItem>
								{playerConfigs.length === 0 ? (
									<MenuItem key={0} value={0}>
										No Player Configurations available
									</MenuItem>
								) : (
									playerConfigs.map(({ name, id }: any) => {
										return (
											<MenuItem key={id} value={id}>
												{name.length > 60 ? `${name.substring(0, 60)}` : name}
											</MenuItem>
										);
									})
								)}
							</Select>
						}
						label={<span style={{ fontWeight: 'normal' }}>{t('COMMON_PLAYER')}</span>}
						labelPlacement={'top'}
					/>

					<div style={{ display: 'flex', alignItems: 'center' }}>
						<Checkbox
							id="hero-textBox"
							color="primary"
							checked={isShowTextBox}
							size="small"
							onChange={(_, checked: boolean) => {
								handleUpdateHeroMedia('isShowTextBox', checked);
							}}
						/>
						<label htmlFor="hero-textBox">Show text box</label>
					</div>

					{hasAttachedForm &&
						<div style={{ display: 'flex', alignItems: 'center' }}>
							<Checkbox
								id="hero-use-attached-form"
								color="primary"
								disabled={!hasAttachedForm}
								checked={useAttachedForm}
								size="small"
								onChange={(_, checked: boolean) => {
									handleUpdateHeroMedia('useAttachedForm', checked);
								}}
							/>
							<label htmlFor="hero-use-attached-form">Enable form template</label>
						</div>
					}

					<FormControlLabel
						className={'inspect-media__input--full'}
						style={{ display: 'flex', marginLeft: 30 }}
						control={
							<OutlinedInput
								value={contentHeroHeader}
								onChange={(
									event: React.ChangeEvent<{
										name?: string;
										value: unknown;
									}>
								) => {
									const value = event.target.value as string;
									handleUpdateHeroMedia('contentHeroHeader', value);
								}}
							/>
						}
						label={
							<span className={'inspect-media__input-label inspect-media__input-label-short'}>
								<Typography variant="body1">Header</Typography>
							</span>
						}
						labelPlacement={'start'}
					/>
					<FormControlLabel
						className={'inspect-media__input--full'}
						style={{ display: 'flex', marginLeft: 30 }}
						control={
							<OutlinedInput
								value={contentHeroDescription}
								onChange={(
									event: React.ChangeEvent<{
										name?: string;
										value: unknown;
									}>
								) => {
									const value = event.target.value as string;
									handleUpdateHeroMedia('contentHeroDescription', value);
								}}
								multiline
								minRows={10}
							/>
						}
						label={
							<span className={'inspect-media__input-label inspect-media__input-label-short'}>
								<Typography variant="body1">Description</Typography>
							</span>
						}
						labelPlacement={'start'}
					/>

					<div style={{ display: 'flex', alignItems: 'center' }}>
						<Checkbox
							id="hero-simple-heading"
							color="primary"
							checked={!!enableSimpleHeroHeading}
							size="small"
							onChange={(_, checked: boolean) => {
								handleUpdateHeroMedia('enableSimpleHeroHeading', checked);
							}}
						/>
						<label htmlFor="hero-simple-heading">Enable Simple Hero heading</label>
					</div>
				</div>
			)}
		</>
	);
};

export default HeroVideoContent;
